import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Pause, Play, Map as MapIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { LEGEND, fetchForecastGrid, fetchRadarFrames, renderField, tweenFrames, valueAt } from "@/lib/rainGrid";

const DARK = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}";
const LABELS = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}";
const BLANK = "data:image/gif;base64,R0lGODlhAQABAAAAACw=";

const trackIcon = (name) => L.divIcon({
  className: "",
  iconSize: [0, 0],
  html: `<div class="wx-track"><span class="wx-track-ring"></span><span class="wx-track-dot"></span><span class="wx-track-label">${name.replace(/</g, "&lt;")}</span></div>`,
});

function useBaseMap(div, lat, lon, name) {
  const [map, setMap] = useState(null);
  useEffect(() => {
    const m = L.map(div.current, { zoomControl: true, attributionControl: true, minZoom: 7, maxZoom: 13 }).setView([lat, lon], 10);
    L.tileLayer(DARK, { maxZoom: 13, attribution: "Esri · RainViewer · OpenWeather · Open-Meteo" }).addTo(m);
    ["wxRain", "wxLabels", "wxTrack"].forEach((p, i) => { m.createPane(p); m.getPane(p).style.zIndex = 420 + i * 20; m.getPane(p).style.pointerEvents = "none"; });
    L.tileLayer(LABELS, { maxZoom: 13, pane: "wxLabels" }).addTo(m);
    L.marker([lat, lon], { icon: trackIcon(name || ""), pane: "wxTrack", interactive: false }).addTo(m);
    setMap(m);
    return () => { m.off(); m.remove(); setMap(null); };
  }, [div, lat, lon, name]);
  return map;
}

function useForecast(map, lat, lon) {
  const [grid, setGrid] = useState(null);
  const [err, setErr] = useState(false);
  const [usage, setUsage] = useState(null);
  const last = useRef(null);
  useEffect(() => {
    if (!map) return undefined;
    const openMeteo = () => {
      const b = map.getBounds().pad(0.08);
      last.current = { b, z: map.getZoom() };
      return fetchForecastGrid(b);
    };
    const load = async () => {
      try {
        const { data } = await api.get("/weather/owm-grid", { params: { lat, lon } }).catch(() => ({ data: { source: "fallback" } }));
        if (data.usage) setUsage(data.usage);
        if (data.source === "owm") { last.current = null; setGrid(data); setErr(false); return; }
        setGrid(await openMeteo());
        setErr(false);
      } catch { setErr(true); }
    };
    let tm = null;
    const onMove = () => {
      clearTimeout(tm);
      tm = setTimeout(() => {
        const l = last.current;
        if (l && (!l.b.contains(map.getBounds()) || map.getZoom() !== l.z)) openMeteo().then(setGrid).catch(() => setErr(true));
      }, 900);
    };
    load();
    map.on("moveend", onMove);
    const id = setInterval(load, 20 * 60 * 1000);
    return () => { clearInterval(id); clearTimeout(tm); map.off("moveend", onMove); };
  }, [map, lat, lon]);
  return { grid, err, usage };
}

function useOverlay(map, grid, img) {
  const ref = useRef(null);
  useEffect(() => {
    if (!map || !grid) return undefined;
    const o = L.imageOverlay(BLANK, grid.bounds, { pane: "wxRain", opacity: 0.9 }).addTo(map);
    const area = grid.source === "owm" ? L.rectangle(grid.bounds, { pane: "wxRain", color: "#FFB800", weight: 1.5, dashArray: "6 6", fill: false, interactive: false }).addTo(map) : null;
    ref.current = o;
    return () => { map.removeLayer(o); if (area) map.removeLayer(area); ref.current = null; };
  }, [map, grid]);
  useEffect(() => {
    if (!ref.current) return;
    if (img) { ref.current.setUrl(img); ref.current.setOpacity(0.9); } else ref.current.setOpacity(0);
  }, [img, grid]);
}

function useRadar(map, idx) {
  const [radar, setRadar] = useState([]);
  const layers = useRef([]);
  useEffect(() => {
    const load = () => fetchRadarFrames().then(setRadar).catch(() => {});
    load();
    const id = setInterval(load, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (!map) return undefined;
    const ls = radar.map((r) => L.tileLayer(r.url, { pane: "wxRain", opacity: 0, maxNativeZoom: 7, maxZoom: 13 }).addTo(map));
    layers.current = ls;
    return () => { ls.forEach((l) => map.removeLayer(l)); layers.current = []; };
  }, [map, radar]);
  useEffect(() => { layers.current.forEach((l, i) => l.setOpacity(i === idx ? 0.85 : 0)); }, [idx, radar]);
  return radar;
}

const Timeline = ({ keys, frames, radarCount, idx, setIdx, playing, setPlaying, onWindy }) => {
  const { t } = useT();
  const total = radarCount + frames.length;
  const cur = idx >= radarCount ? frames[idx - radarCount] : null;
  const cols = keys.length + (radarCount ? 1 : 0);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" data-testid="rainmap-play-btn" onClick={() => setPlaying(!playing)} className="h-10 w-10 flex items-center justify-center rounded-sm bg-[#FF3B30] text-white hover:bg-[#e0332a] transition-colors duration-200">
        {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
      </button>
      <div data-testid="rainmap-timeline" className="relative flex-1 min-w-[220px] grid gap-1" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {radarCount > 0 && (
          <button type="button" data-testid="rainmap-step-radar" onClick={() => { setPlaying(false); setIdx(0); }}
            className={`h-10 rounded-sm border font-heading font-bold text-sm sm:text-lg uppercase whitespace-nowrap transition-colors duration-200 ${idx < radarCount ? "border-[#007AFF] bg-[#007AFF] text-white" : "border-[#282D3D] text-slate-300 hover:text-white hover:border-slate-500"}`}>
            <span className="hidden sm:inline">{t("wx_kind_radar")} </span>−120'
          </button>
        )}
        {keys.map((k, i) => {
          const fi = frames.findIndex((f) => f.key === i);
          const active = cur && Math.abs(cur.t - k.t) < (keys[1] ? (keys[1].t - keys[0].t) / 2 : 1);
          return (
            <button key={k.t} type="button" data-testid={`rainmap-step-${i}`} onClick={() => { setPlaying(false); setIdx(radarCount + fi); }}
              className={`h-10 rounded-sm border font-heading font-bold text-sm sm:text-lg uppercase whitespace-nowrap transition-colors duration-200 ${active ? "border-[#FFB800] bg-[#FFB800] text-black" : "border-[#282D3D] text-slate-300 hover:text-white hover:border-slate-500"}`}>
              {i === 0 ? t("wx_now") : `+${i * 15}'`}
            </button>
          );
        })}
        <div className="col-span-full h-1 bg-[#282D3D] rounded-full overflow-hidden">
          <div className={`h-full transition-[width] duration-200 ${idx < radarCount ? "bg-[#007AFF]" : "bg-[#FFB800]"}`} style={{ width: `${total > 1 ? (idx / (total - 1)) * 100 : 0}%` }} />
        </div>
      </div>
      <button type="button" data-testid="rainmap-windy-btn" onClick={onWindy} className="h-10 px-3 flex items-center gap-1.5 rounded-sm border border-[#007AFF]/60 bg-[#007AFF]/10 text-[#007AFF] font-mono text-xs uppercase hover:bg-[#007AFF]/20 transition-colors duration-200">
        <MapIcon className="w-4 h-4" /> {t("wx_windy_map")}
      </button>
    </div>
  );
};

export const RainMap = ({ lat, lon, name, offset, h = 480, onWindy }) => {
  const { t } = useT();
  const div = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [idx, setIdx] = useState(0);
  const map = useBaseMap(div, lat, lon, name);
  const { grid, err, usage } = useForecast(map, lat, lon);
  const frames = useMemo(() => (grid ? tweenFrames(grid.keys).map((f) => ({ ...f, img: renderField(f.v, grid.rows, grid.cols) })) : []), [grid]);
  const radar = useRadar(map, idx);
  const R = radar.length;
  const total = R + frames.length;
  const isRadar = idx < R;
  const rf = isRadar ? radar[idx] : null;
  const f = isRadar ? null : frames[Math.min(idx - R, frames.length - 1)];
  useOverlay(map, grid, f?.img);
  useEffect(() => { if (idx >= total) setIdx(0); }, [total, idx]);
  useEffect(() => {
    if (!playing || total < 2) return undefined;
    const id = setTimeout(() => setIdx((i) => (i + 1) % total), idx === total - 1 ? 2200 : idx === R - 1 ? 1200 : isRadar ? 450 : f?.key != null ? 700 : 160);
    return () => clearTimeout(id);
  }, [playing, idx, total, R, isRadar, f?.key]);
  const ft = rf?.t ?? f?.t;
  const mins = ft != null ? Math.round((ft - (grid?.keys[0]?.t ?? Date.now() / 1000)) / 60) : 0;
  const clock = ft != null ? new Date((ft + (offset ?? -new Date().getTimezoneOffset() * 60)) * 1000).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) : "";
  const label = isRadar ? `${mins < 0 ? "−" : ""}${Math.abs(mins)} min` : mins <= 0 ? t("wx_now") : `+${mins} min`;
  const atTrack = f && grid ? valueAt(grid, f.v, lat, lon) : null;
  return (
    <section data-testid="weather-rainmap" className="bg-[#12141C] border border-[#282D3D] rounded-sm p-3 flex flex-col gap-3 h-full">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{t("wx_rain_map")}</div>
          <div data-testid="rainmap-time" className="font-heading font-extrabold text-3xl leading-none tabular-nums mt-1 flex items-center gap-2">
            <span data-testid="rainmap-kind" className={`px-2 py-0.5 rounded-sm text-base ${isRadar ? "bg-[#007AFF] text-white" : "bg-[#FFB800] text-black"}`}>{t(isRadar ? "wx_kind_radar" : "wx_kind_fc")}</span>
            {label} <span className="text-slate-400 text-2xl">· {clock}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {atTrack != null && (
            <div data-testid="rainmap-track-value" className={`font-heading font-bold text-2xl tabular-nums ${atTrack >= 0.1 ? "text-[#60A5FA]" : "text-slate-500"}`}>{t("wx_on_track")} {atTrack.toFixed(1)} mm/h</div>
          )}
          {grid && (
            <div data-testid="rainmap-source" data-source={grid.source} className="font-mono text-[10px] uppercase text-slate-500">
              {grid.source === "owm" ? "OpenWeather" : "Open-Meteo"}{usage ? ` · ${t("wx_owm_usage").replace("{n}", usage.count).replace("{cap}", usage.cap)}` : ""}
            </div>
          )}
        </div>
      </div>
      {err && <div data-testid="rainmap-fc-error" className="font-mono text-[11px] text-[#FFB800]">{t("wx_fc_unavailable")}</div>}
      <div className="relative flex-1 rounded-sm overflow-hidden border border-[#282D3D]" style={{ minHeight: h }}>
        <div ref={div} className="absolute inset-0 z-0" />
        <div className="absolute left-2 bottom-2 z-[500] bg-[#090A0F]/85 rounded-sm px-2 py-1.5 flex items-center gap-1 font-mono text-[10px] text-slate-300">
          {LEGEND.map(([v, c]) => <span key={v} className="flex items-center gap-0.5"><span className="w-3 h-2.5 rounded-[1px]" style={{ background: c }} />{v}</span>)}
          <span className="ml-1 text-slate-500">mm/h</span>
        </div>
      </div>
      {grid && <Timeline keys={grid.keys} frames={frames} radarCount={R} idx={Math.min(idx, total - 1)} setIdx={setIdx} playing={playing} setPlaying={setPlaying} onWindy={onWindy} />}
    </section>
  );
};
